/**
 * «Автотрейдинг» — стратегия по таймеру (config/autotrading.ts).
 *
 * Панель по макету заказчика (2026-09-18): карточка счёта с балансом и P&L
 * сессии, карточка стратегии с тумблером запуска и тремя пилюлями текущих
 * настроек (сами настройки свёрнуты — раскрываются тапом по пилюле), карточка
 * открытых сделок с прогресс-баром и список сделок сессии. Сессия идёт на
 * сервере, экран опрашивает её состояние.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api, apiMessage } from '../lib/api.js';
import type {
  AutotradingPace,
  AutotradingParams,
  AutotradingSession,
  SettingsPayload,
} from '../lib/api.js';
import { money, payoutPercent } from '../lib/format.js';
import { haptic } from '../lib/telegram.js';
import { useApp } from '../state/app.js';

const POLL_MS = 2_000;
const POLL_MAX_MS = 15_000;
const POLL_GIVE_UP = 12;

type Translate = (key: string, params?: Record<string, string | number>) => string;

const FALLBACK_OPTIONS = {
  percent: [0.5, 1, 2, 3, 5],
  durationMinutes: [15, 30, 60, 120],
  timeframeSeconds: [5, 15, 30, 60, 180, 300],
  pace: ['fast', 'normal', 'calm'] as AutotradingPace[],
  sessionLimitPercent: [0, 5, 10, 20],
  tokensPerTrade: 2,
  enabled: true,
};

function timeframeLabel(t: Translate, seconds: number): string {
  if (seconds === 0) return t('atr.value.random');
  return seconds < 60
    ? t('atr.timeframe.seconds', { value: seconds })
    : t('atr.timeframe.minutes', { value: Math.round(seconds / 60) });
}

function limitLabel(t: Translate, percent: number): string {
  return percent === 0 ? t('atr.value.off') : t('atr.percent', { value: percent });
}

function clock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s < 10 ? '0' : ''}${s}`;
}

const signed = (value: number): string => `${value >= 0 ? '+' : '−'}$${money(Math.abs(value))}`;

function Chips<T extends number | string>({
  values,
  current,
  label,
  onPick,
  cols = 3,
}: {
  values: readonly T[];
  current: T;
  label: (value: T) => string;
  onPick: (value: T) => void;
  cols?: number;
}) {
  return (
    <div className="chip-grid" style={{ '--chip-cols': cols } as React.CSSProperties}>
      {values.map((value) => (
        <button
          key={String(value)}
          type="button"
          className={`chip ${current === value ? 'is-active' : ''}`}
          onClick={() => {
            haptic();
            onPick(value);
          }}
        >
          <span className="chip-label">{label(value)}</span>
        </button>
      ))}
    </div>
  );
}

export function AutotradingPanel({
  settings,
  onSettings,
  brandName,
}: {
  settings: SettingsPayload;
  onSettings: (next: SettingsPayload) => void;
  /** Имя бренда из конфига (CLAUDE.md §0.1) — в кикер, не хардкодом. */
  brandName: string;
}) {
  const { me, patchMe, showToast, setTab, t } = useApp();
  const options = settings.options.autotrading ?? FALLBACK_OPTIONS;
  const params = settings.current.autotrading;

  const [session, setSession] = useState<AutotradingSession | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const watching = useRef<string | null>(null);
  const pollFailures = useRef(0);

  const running = session?.status === 'running';
  const finished = session !== null && !running;

  /** Секундная стрелка: таймер сессии, прогресс открытой сделки. */
  useEffect(() => {
    if (!running) return;
    const timer = globalThis.setInterval(() => setNow(Date.now()), 1_000);
    return () => globalThis.clearInterval(timer);
  }, [running]);

  const apply = useCallback(
    (next: AutotradingSession) => {
      if (!next || typeof next.status !== 'string' || !Array.isArray(next.trades)) {
        showToast(t('app.trade.outdated'));
        return;
      }
      setSession(next);
      if (next.status === 'running') return;
      watching.current = null;
      if (me) {
        patchMe({
          user: { ...me.user, tokens: next.tokensLeft },
          balances:
            next.market === 'real'
              ? { ...me.balances, real: next.balance }
              : { ...me.balances, demo: next.balance },
        });
      }
    },
    [me, patchMe, showToast, t],
  );

  /** Опрос идущей сессии — с растущей паузой и потолком попыток при сбоях. */
  useEffect(() => {
    if (!session || session.status !== 'running') {
      pollFailures.current = 0;
      return;
    }
    watching.current = session.sessionId;
    let cancelled = false;
    const delay = Math.min(POLL_MS * 2 ** pollFailures.current, POLL_MAX_MS);
    const timer = globalThis.setTimeout(() => {
      void api
        .autotradingSession(session.sessionId)
        .then((next) => {
          pollFailures.current = 0;
          if (!cancelled && watching.current === next.sessionId) apply(next);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          const status = err instanceof ApiError ? err.status : 0;
          pollFailures.current += 1;
          if (status === 401 || status === 404 || pollFailures.current >= POLL_GIVE_UP) {
            watching.current = null;
            pollFailures.current = 0;
            setSession(null);
            if (status !== 401) showToast(t('app.error.load_failed'));
            return;
          }
          setSession((current) => (current ? { ...current } : current));
        });
    }, delay);
    return () => {
      cancelled = true;
      globalThis.clearTimeout(timer);
    };
  }, [session, apply, showToast, t]);

  /** Приложение открыли посреди сессии — подхватываем её. */
  useEffect(() => {
    void api
      .autotradingActive()
      .then(({ session: active }) => {
        if (active) apply(active);
      })
      .catch(() => undefined);
    // один раз при монтировании: дальше состояние ведёт опрос
  }, []);

  /** Настройка меняется оптимистично; сервер не принял — откат и тост. */
  const setParam = (key: keyof AutotradingParams, value: number | string): void => {
    const previous = params;
    const next: AutotradingParams = { ...params, [key]: value } as AutotradingParams;
    // диапазон ставки сшивается, как на сервере: «от» не бывает больше «до»
    if (key === 'minPercent' && next.maxPercent < Number(value)) next.maxPercent = Number(value);
    if (key === 'maxPercent' && next.minPercent > Number(value)) next.minPercent = Number(value);
    const withParams = (p: AutotradingParams): SettingsPayload => ({
      ...settings,
      current: { ...settings.current, autotrading: p },
    });
    onSettings(withParams(next));
    api.updateSettings({ autotrading: { [key]: value } }).catch(() => {
      onSettings(withParams(previous));
      showToast(t('app.account.save_failed'));
    });
  };

  async function start(): Promise<void> {
    if (busy) return;
    haptic('medium');
    setBusy(true);
    setEditing(false);
    try {
      apply(await api.autotradingStart());
    } catch (err) {
      if (err instanceof ApiError && err.isOutOfTokens) {
        showToast(t('app.trade.no_tokens'));
        setTab('tokens');
        return;
      }
      const payload =
        err instanceof ApiError ? (err.payload as { error?: string; reason?: string } | null) : null;
      if (payload?.error === 'autotrading_unavailable') {
        showToast(t(`atr.stop.${payload.reason ?? 'disabled'}`));
        return;
      }
      showToast(apiMessage(err, t, 'app.trade.failed'));
      // аккаунт не привязан или сессия у брокера истекла — ведём туда, где это чинится
      if (payload?.error === 'account_required' || payload?.error === 'broker_session_expired') {
        setTab('account');
      }
    } finally {
      setBusy(false);
    }
  }

  async function stop(): Promise<void> {
    if (busy) return;
    haptic('rigid');
    setBusy(true);
    try {
      const { session: next } = await api.autotradingStop();
      if (next) setSession(next);
    } catch (err) {
      showToast(apiMessage(err, t, 'app.trade.failed'));
    } finally {
      setBusy(false);
    }
  }

  const toggle = (): void => {
    if (running) void stop();
    else void start();
  };

  // --- цифры для карточки счёта ---
  const mode = me?.user.mode ?? 'DEMO';
  const accountBalance = mode === 'REAL' ? (me?.balances.real ?? 0) : (me?.balances.demo ?? 0);
  const balance = running && session ? session.balance : accountBalance;
  const sessionPnl = session?.profit ?? 0;
  const startBalance = session?.startBalance ?? accountBalance;
  const stakeMin = Math.max(1, (balance * params.minPercent) / 100);
  const stakeMax = Math.max(1, (balance * params.maxPercent) / 100);

  const openTrades = session?.pending ? 1 : 0;
  const left = session ? session.endsAt - now : 0;
  const pending = running ? session?.pending : null;
  const pendingTotal = pending ? pending.timeframeSeconds * 1000 : 0;
  const pendingElapsed = pending ? Math.min(pendingTotal, now - pending.openedAt) : 0;
  const pendingLeft = pending ? Math.max(0, Math.ceil((pendingTotal - pendingElapsed) / 1000)) : 0;

  const statusText = running
    ? session?.claimedAt === null
      ? t('app.atr.status_starting')
      : session?.stopRequested
        ? t('app.atr.stopping')
        : t('app.atr.status_running', { trades: openTrades, left: clock(left) })
    : t('app.atr.status_idle');

  return (
    <div className="screen">
      {/* счёт: баланс, P&L сессии, статус */}
      <section className="card card-lg">
        <div className="row-between">
          <span className="kicker">{t('app.atr.account')}</span>
          <span className={`pill ${mode === 'REAL' ? 'pill-green' : ''}`}>● {mode}</span>
        </div>
        <div className="atr-balance-row">
          <div>
            <div className="label">{t('app.atr.balance')}</div>
            <div className="atr-balance">${money(balance)}</div>
          </div>
          <div className="atr-pnl">
            <div className="label">{t('app.atr.session_pnl')}</div>
            <div
              className={`atr-pnl-value ${sessionPnl > 0 ? 'is-win' : sessionPnl < 0 ? 'is-loss' : ''}`}
            >
              {signed(sessionPnl)}
            </div>
          </div>
        </div>
        <div className="row-between atr-muted">
          <span>{t('app.atr.session_start')}</span>
          <strong>${money(startBalance)}</strong>
        </div>
        <div className={`atr-status ${running ? 'is-on' : ''}`}>
          <span className="atr-dot" />
          <span>{statusText}</span>
        </div>
      </section>

      {/* стратегия: тумблер, пилюли настроек, кнопка */}
      <section className="card card-lg">
        <div className="atr-head">
          <div>
            <span className="kicker">{brandName ? `${brandName} AI` : 'AI'}</span>
            <h2 className="atr-title">{t('app.atr.title')}</h2>
          </div>
          <button
            type="button"
            className={`atr-toggle ${running ? 'is-on' : ''}`}
            aria-pressed={running}
            disabled={busy || !options.enabled || (running && session?.stopRequested)}
            onClick={toggle}
          />
        </div>
        <p className="body">{t('app.atr.mirror_note')}</p>

        {/* настройки свёрнуты в пилюли; тап раскрывает редактор, пока сессия не идёт */}
        <div className="atr-pills">
          <button
            type="button"
            className={`atr-pill ${running ? 'is-locked' : ''}`}
            onClick={() => !running && setEditing((v) => !v)}
          >
            <span className="label">{t('app.atr.pill_stake')}</span>
            <span className="atr-pill-value">
              ${money(stakeMin)}–{money(stakeMax)}
            </span>
          </button>
          <button
            type="button"
            className={`atr-pill ${running ? 'is-locked' : ''}`}
            onClick={() => !running && setEditing((v) => !v)}
          >
            <span className="label">{t('app.atr.pill_time')}</span>
            <span className="atr-pill-value">{timeframeLabel(t, params.timeframeSeconds)}</span>
          </button>
          <button
            type="button"
            className={`atr-pill ${running ? 'is-locked' : ''}`}
            onClick={() => !running && setEditing((v) => !v)}
          >
            <span className="label">{t('app.atr.pill_session')}</span>
            <span className="atr-pill-value">{t('app.atr.minutes', { value: params.durationMinutes })}</span>
          </button>
        </div>

        {editing && !running && (
          <div className="atr-editor">
            <div>
              <div className="label">
                {t('app.atr.percent')} · {t('app.atr.from')}
              </div>
              <Chips
                values={options.percent}
                current={params.minPercent}
                label={(v) => `${v} %`}
                onPick={(v) => setParam('minPercent', v)}
                cols={5}
              />
              <div className="label">
                {t('app.atr.percent')} · {t('app.atr.to')}
              </div>
              <Chips
                values={options.percent}
                current={params.maxPercent}
                label={(v) => `${v} %`}
                onPick={(v) => setParam('maxPercent', v)}
                cols={5}
              />
            </div>
            <div>
              <div className="label">{t('app.atr.duration')}</div>
              <Chips
                values={options.durationMinutes}
                current={params.durationMinutes}
                label={(v) => t('app.atr.minutes', { value: v })}
                onPick={(v) => setParam('durationMinutes', v)}
                cols={4}
              />
            </div>
            <div>
              <div className="label">{t('app.atr.timeframe')}</div>
              <Chips
                values={[0, ...options.timeframeSeconds]}
                current={params.timeframeSeconds}
                label={(v) => timeframeLabel(t, v)}
                onPick={(v) => setParam('timeframeSeconds', v)}
                cols={4}
              />
            </div>
            <div>
              <div className="label">{t('app.atr.pace')}</div>
              <Chips
                values={options.pace}
                current={params.pace}
                label={(v) => t(`atr.pace.${v}`)}
                onPick={(v) => setParam('pace', v)}
                cols={3}
              />
            </div>
            <div>
              <div className="label">{t('app.atr.stop_loss')}</div>
              <Chips
                values={options.sessionLimitPercent}
                current={params.stopLossPercent}
                label={(v) => limitLabel(t, v)}
                onPick={(v) => setParam('stopLossPercent', v)}
                cols={4}
              />
            </div>
            <div>
              <div className="label">{t('app.atr.take_profit')}</div>
              <Chips
                values={options.sessionLimitPercent}
                current={params.takeProfitPercent}
                label={(v) => limitLabel(t, v)}
                onPick={(v) => setParam('takeProfitPercent', v)}
                cols={4}
              />
            </div>
            <button type="button" className="cta cta-secondary cta-block" onClick={() => setEditing(false)}>
              {t('app.atr.done')}
            </button>
          </div>
        )}

        {!editing && !running && <p className="step-hint">{t('app.atr.settings_hint')}</p>}

        <button
          type="button"
          className={`cta cta-launch cta-block ${running ? 'is-running' : ''}`}
          disabled={busy || !options.enabled || (running && session?.stopRequested)}
          onClick={toggle}
        >
          {!options.enabled
            ? t('app.atr.unavailable')
            : running
              ? session?.stopRequested
                ? t('app.atr.stopping')
                : t('app.atr.running_button')
              : t('app.atr.start')}
        </button>
      </section>

      {/* открытые сделки: одна за раз, с прогрессом до закрытия */}
      {running && session && (
        <section className="card">
          <div className="row-between">
            <span className="kicker">{t('app.atr.in_market')}</span>
            <span className="atr-count">{openTrades}</span>
          </div>
          <div className="atr-list">
            {pending ? (
              <div className={`atr-trade ${pending.action === 'call' ? 'is-up' : 'is-down'}`}>
                <div className="atr-trade-row">
                  <span className="atr-trade-pair">{pending.pairLabel}</span>
                  <span className="atr-trade-dir">{pending.action === 'call' ? '↑ UP' : '↓ DOWN'}</span>
                </div>
                <div className="atr-trade-row atr-trade-meta">
                  <span>
                    ${money(pending.amount)}
                    {pending.payout !== null && ` · ${t('app.atr.payout', { value: payoutPercent(pending.payout) })}`}
                  </span>
                  <span>{pendingLeft} s</span>
                </div>
                <div className="atr-progress">
                  <div
                    className="atr-progress-fill"
                    style={{ width: `${pendingTotal > 0 ? (pendingElapsed / pendingTotal) * 100 : 0}%` }}
                  />
                </div>
              </div>
            ) : (
              <p className="step-hint">
                {session.nextTradeAt
                  ? t('app.atr.next_in', { seconds: Math.max(0, Math.ceil((session.nextTradeAt - now) / 1000)) })
                  : t('app.atr.no_open')}
              </p>
            )}
          </div>
        </section>
      )}

      {/* итог завершённой сессии */}
      {finished && session && (
        <section className="card card-lg victory-card">
          <h2 className="card-title">{t('app.atr.finished')}</h2>
          <p className="body">{t(`atr.stop.${session.stopReason ?? 'time'}`)}</p>
          <div className="victory-grid">
            <div>
              <div className="label">{t('app.atr.trades')}</div>
              <div className="victory-value">
                {session.trades.length} · {session.wonTrades} ✓
              </div>
            </div>
            <div>
              <div className="label">{t('app.atr.result')}</div>
              <div className={`victory-value ${session.profit >= 0 ? 'is-win' : 'is-loss'}`}>
                {signed(session.profit)}
              </div>
            </div>
            <div>
              <div className="label">{t('app.trade.tokens_spent')}</div>
              <div className="victory-value">{session.tokensSpent} 💎</div>
            </div>
          </div>
          <div className="row">
            <button type="button" className="cta cta-launch grow" onClick={() => setSession(null)}>
              {t('app.trade.again')}
            </button>
            <button type="button" className="cta cta-secondary grow" onClick={() => setTab('home')}>
              {t('app.trade.to_menu')}
            </button>
          </div>
        </section>
      )}

      {/* закрытые сделки сессии, свежие сверху */}
      {session && session.trades.length > 0 && (
        <section className="card">
          <div className="row-between">
            <span className="kicker">{t('app.atr.session_trades')}</span>
            <span className="atr-count">{session.trades.length}</span>
          </div>
          <div className="atr-list">
            {[...session.trades].reverse().map((trade) => (
              <div key={trade.index} className={`atr-trade ${trade.won ? 'is-win' : 'is-loss'}`}>
                <div className="atr-trade-row">
                  <span className="atr-trade-pair">{trade.pairLabel}</span>
                  <span className="atr-trade-result">{signed(trade.profit)}</span>
                </div>
                <div className="atr-trade-row atr-trade-meta">
                  <span>
                    {trade.action === 'call' ? '↑ UP' : '↓ DOWN'} · ${money(trade.amount)}
                    {trade.payout !== null && ` · ${t('app.atr.payout', { value: payoutPercent(trade.payout) })}`}
                  </span>
                  <span>{timeframeLabel(t, trade.timeframeSeconds)}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="card">
        <div className="row-between">
          <span className="label">{t('app.trade.tokens_available')}</span>
          <span className="pill">{me?.user.tokens ?? 0} 💎</span>
        </div>
        <p className="body">{t('app.atr.cost_note', { cost: options.tokensPerTrade })}</p>
      </section>
    </div>
  );
}
